import { useState, useEffect, useMemo } from 'react';
import { fetchSessionUserWithMeta } from '../utils/authSessionApi';
import {
  setUserClaimsSnapshot,
  getAuthHeaders,
  getUserClaimsForUi,
} from '../utils/authStorage';

/**
 * HttpOnly cookie auth: load current user from `GET /api/auth/me`.
 * `/me` fail + **401** → error; **520 / network** → cached `getUserClaimsForUi()` se chalao (Estimation redirect na ho).
 *
 * @returns {{ user: object|null, loading: boolean, error: 'unauthorized'|null, config: object, meUnreachable: boolean }}
 */
export function useSessionUserFromMe() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meUnreachable, setMeUnreachable] = useState(false);

  const config = useMemo(() => getAuthHeaders(), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setMeUnreachable(false);
      try {
        const { user: me, meta } = await fetchSessionUserWithMeta();
        if (cancelled) return;

        if (me?.id) {
          setUserClaimsSnapshot(me);
          setUser(me);
          return;
        }

        const transportDown = meta === 'server_error' || meta === 'network';
        if (transportDown) {
          const fallback = getUserClaimsForUi();
          if (fallback?.id && typeof fallback.id === 'object') {
            setUser(fallback);
            setMeUnreachable(true);
            return;
          }
        }

        setUser(null);
        setError('unauthorized');
      } catch {
        if (!cancelled) {
          const fallback = getUserClaimsForUi();
          if (fallback?.id && typeof fallback.id === 'object') {
            setUser(fallback);
            setMeUnreachable(true);
          } else {
            setUser(null);
            setError('unauthorized');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { user, loading, error, config, meUnreachable };
}
